'use client';

import { useEffect, useRef, useCallback } from 'react';

interface UseInfiniteScrollOptions {
  onLoadMore: () => Promise<void>;
  hasMore: boolean;
  isLoading: boolean;
  rootMargin?: string;
  threshold?: number | number[];
}

/**
 * Custom hook for infinite scroll with Intersection Observer
 * 
 * Separation of Duties:
 * - Handles all Intersection Observer logic
 * - Manages trigger element lifecycle
 * - Prevents multiple simultaneous loads
 * - Components only need to attach ref and provide callbacks
 */
export function useInfiniteScroll({
  onLoadMore,
  hasMore,
  isLoading,
  rootMargin = '100px',
  threshold = 0.1,
}: UseInfiniteScrollOptions) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isLoadingRef = useRef(false);

  /**
   * Setup Intersection Observer
   * This runs whenever dependencies change
   */
  useEffect(() => {
    // Guard: Don't observe if no more items or already loading
    if (!hasMore || isLoading) {
      // Clean up observer if conditions no longer met
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    // Guard: Need a trigger element to observe
    if (!triggerRef.current) {
      return;
    }

    // Get the scrollable container (parent with overflow-y-auto)
    const scrollContainer = triggerRef.current.closest('.overflow-y-auto');
    if (!scrollContainer) {
      return;
    }

    /**
     * Intersection Observer callback
     * Triggers when the element becomes visible in the scroll container
     */
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;

      // Guard: Prevent multiple simultaneous loads
      if (entry.isIntersecting && !isLoadingRef.current && hasMore) {
        isLoadingRef.current = true;
        onLoadMore()
          .catch(err => console.error('Failed to load more:', err))
          .finally(() => {
            isLoadingRef.current = false;
          });
      }
    };

    // Create observer with specified options
    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: scrollContainer,
      rootMargin,
      threshold,
    });

    // Start observing the trigger element
    observerRef.current.observe(triggerRef.current);

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [hasMore, isLoading, onLoadMore, rootMargin, threshold]);

  return triggerRef;
}














